import EditUserDetail from 'components/EditUserDetail/EditUserDetail';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';

import { UseFormReturn, Controller } from 'react-hook-form';
import { SEGMENT_TYPES } from 'shared/constants/SEGMENT_TYPES';
import { IProgramSetup, IProgramSetupForm, ProgramSetupUser } from 'shared/interfaces/IProgramSetup';
import { Input, Select } from 'shared/theme/elements';
import { AnchorMultiSelect } from './components/AnchorMultiSelect';
import {
  Card,
  CardBody,
  CardHead,
  CardIc,
  CardSub,
  CardTitle,
  CardTitles,
  Fld,
  Grid3,
  Note,
} from './ProgramSetup.styled';
import {
  ProgramSetupPageSearchLoading,
  ProgramSetupPageSearchOptions,
  ProgramSetupSearch,
} from './ProgramSetupContainer';

interface Props {
  form: UseFormReturn<IProgramSetupForm, object>;
  isLoading?: boolean;
  options: ProgramSetupPageSearchOptions;
  searchLoading: ProgramSetupPageSearchLoading;
  onSearch: (key: keyof ProgramSetupSearch, value: any) => void;
  programSetup?: IProgramSetup;
}

export const ProgramSetupDetails = ({ form, isLoading, options, searchLoading, onSearch, programSetup }: Props) => {
  const {
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = form;

  const isUpdate = useMemo(() => !!programSetup, [programSetup]);
  const channelDisabled = isLoading || isEmpty(getValues().channel_obj) ? true : false;

  const handleChannelChange = (value: any) => {
    clearErrors('channel_obj');
    setValue('channel_obj', value ? (value as string) : '');
    setValue('program_obj', '');
  };

  const handleProgramChange = (value: any) => {
    clearErrors('program_obj');
    setValue('program_obj', value ? (value as string) : '');
  };

  const handleRadioStationChange = (value: any) => {
    clearErrors('station_program_obj');
    setValue('station_obj', value ? (value as string) : null);
    setValue('station_program_obj', null);
  };

  const handleRadioProgramChange = (value: any) => {
    clearErrors('station_program_obj');
    setValue('station_program_obj', value ? (value as string) : null);
  };

  const handleCountryChange = (value: any) => {
    clearErrors('country');
    setValue('country', value as string);
  };

  const handleSegmentTypeChange = (value: any) => {
    clearErrors('segment_type');
    setValue('segment_type', value ? (value as string) : null);
  };

  const getUserName = (user?: ProgramSetupUser) => {
    return user ? `${user.first_name} ${user.last_name}` : '';
  };

  return (
    <Card id="secDetails">
      <CardHead>
        <CardIc>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6M8 13h8M8 17h5" />
          </svg>
        </CardIc>
        <CardTitles>
          <CardTitle>Program Details</CardTitle>
          <CardSub>Basic information about the program</CardSub>
        </CardTitles>
      </CardHead>
      <CardBody>
        {isUpdate && (
          <Grid3 style={{ marginBottom: 20 }}>
            <EditUserDetail
              label="Created by"
              date={new Date(programSetup?.date_created || '')}
              name={getUserName(programSetup?.created_by?.[0])}
            />
            {programSetup && programSetup?.updated_by !== null && (
              <EditUserDetail
                label="Modified by"
                date={new Date(programSetup?.date_updated || '')}
                name={getUserName(programSetup?.updated_by?.[0])}
              />
            )}
          </Grid3>
        )}

        <Grid3>
          <Fld>
            <Select
              label="Channel"
              placeHolder="Select Channel"
              size="middle"
              value={getValues().channel_obj}
              options={options.tvChannel || []}
              onChange={handleChannelChange}
              errorMessage={errors.channel_obj?.message}
              onSearch={(keyword) => onSearch('tvChannel', keyword)}
              filterOption={false}
              showSearch
              autoClearSearchValue
              loading={searchLoading.tvChannel}
              isRequired
              $fullWidth
            />
          </Fld>
          <Fld>
            <Select
              label="Program"
              placeHolder="Select Program"
              size="middle"
              value={isEmpty(getValues('program_obj')) ? undefined : getValues('program_obj')}
              options={options.tvProgram || []}
              onChange={handleProgramChange}
              errorMessage={errors.program_obj?.message}
              disabled={channelDisabled}
              onSearch={(keyword) => onSearch('tvProgram', keyword)}
              showSearch
              filterOption={false}
              autoClearSearchValue
              loading={searchLoading.tvProgram}
              isRequired
              $fullWidth
            />
          </Fld>
          <Fld>
            <Select
              label="Segment Type"
              placeHolder="Select Segment Type"
              size="middle"
              value={isEmpty(getValues().segment_type) ? undefined : getValues().segment_type}
              options={SEGMENT_TYPES}
              onChange={handleSegmentTypeChange}
              errorMessage={errors.segment_type?.message}
              disabled={channelDisabled}
              isRequired
              $fullWidth
            />
          </Fld>

          <Fld>
            <Select
              label="Radio Station"
              placeHolder="Select Radio Station"
              size="middle"
              value={isEmpty(getValues('station_obj')) ? undefined : getValues('station_obj')}
              options={options.radioStation || []}
              onChange={handleRadioStationChange}
              errorMessage={errors.station_obj?.message}
              disabled={channelDisabled}
              onSearch={(keyword) => onSearch('radioStation', keyword)}
              showSearch
              filterOption={false}
              autoClearSearchValue
              loading={searchLoading.radioStation}
              allowClear
              $fullWidth
            />
          </Fld>
          <Fld>
            <Select
              label="Radio Program"
              placeHolder="Select Radio Program"
              size="middle"
              value={isEmpty(getValues().station_program_obj) ? undefined : getValues().station_program_obj}
              options={options.radioStationProgram || []}
              onChange={handleRadioProgramChange}
              errorMessage={errors.station_program_obj?.message}
              disabled={channelDisabled}
              onSearch={(keyword) => onSearch('radioStationProgram', keyword)}
              showSearch
              filterOption={false}
              autoClearSearchValue
              allowClear
              loading={searchLoading.radioStationProgram}
              $fullWidth
              isRequired={!isEmpty(getValues().station_obj)}
            />
          </Fld>
          <Fld>
            <Select
              label="Country"
              placeHolder="Select Country"
              size="middle"
              value={isEmpty(getValues().country) ? undefined : getValues().country}
              options={options.country || []}
              onChange={handleCountryChange}
              errorMessage={errors.country?.message}
              disabled={channelDisabled}
              onSearch={(keyword) => onSearch('country', keyword)}
              showSearch
              filterOption={false}
              autoClearSearchValue
              loading={searchLoading.country}
              isRequired
              $fullWidth
            />
          </Fld>
        </Grid3>
      </CardBody>
    </Card>
  );
};

export const ProgramSetupAnchors = ({ form, isLoading, options, searchLoading, onSearch }: Props) => {
  const {
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = form;
  const channelDisabled = isLoading || isEmpty(getValues().channel_obj) ? true : false;

  return (
    <Card id="secAnchors">
      <CardHead>
        <CardIc $tone="ppl">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </CardIc>
        <CardTitles>
          <CardTitle>Anchor / Reporter</CardTitle>
          <CardSub>Select one or more anchors or reporters</CardSub>
        </CardTitles>
      </CardHead>
      <CardBody>
        <AnchorMultiSelect
          value={getValues().anchors}
          options={options.authors}
          onChange={(anchors) => {
            setValue('anchors', anchors);
            clearErrors('anchors');
          }}
          onSearch={(keyword) => onSearch('authors', keyword)}
          loading={searchLoading.authors}
          disabled={channelDisabled}
        />
        {errors.anchors?.message && (
          <div style={{ marginTop: 7, fontSize: 11.5, color: 'var(--req)', fontWeight: 600 }}>
            {errors.anchors?.message as string}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export const ProgramSetupRecording = ({ form, isLoading }: Props) => {
  const {
    getValues,
    control,
    formState: { errors },
  } = form;
  const channelDisabled = isLoading || isEmpty(getValues().channel_obj) ? true : false;

  return (
    <Card id="secRecording">
      <CardHead>
        <CardIc $tone="rec">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="6" width="14" height="12" rx="2" />
            <path d="m22 8-6 4 6 4V8Z" />
          </svg>
        </CardIc>
        <CardTitles>
          <CardTitle>Recording Configuration</CardTitle>
          <CardSub>Sagap recording settings for this program</CardSub>
        </CardTitles>
      </CardHead>
      <CardBody>
        <Grid3>
          <Fld>
            <Controller
              control={control}
              name="local_recording_details.local_recording_path"
              render={({ field: { value, onBlur, onChange } }) => (
                <Select
                  label="Recording Machine Name"
                  placeHolder="Select Recording Machine"
                  size="middle"
                  value={value || undefined}
                  items={[
                    { id: 'tv1', value: 'tv1' },
                    { id: 'tv2', value: 'tv2' },
                    { id: 'tv3', value: 'tv3' },
                    { id: 'tv4', value: 'tv4' },
                    { id: 'tv5', value: 'tv5' },
                  ]}
                  onChange={onChange}
                  errorMessage={errors.local_recording_details?.local_recording_path?.message}
                  disabled={channelDisabled}
                  isRequired
                  $fullWidth
                  onBlur={onBlur}
                />
              )}
            />
          </Fld>
          <Fld>
            <Controller
              control={control}
              name="local_recording_details.source_folder"
              render={({ field: { value, onBlur, onChange } }) => (
                <Select
                  label="Source Folder"
                  placeHolder="Select Source Folder"
                  size="middle"
                  value={value || undefined}
                  items={[
                    { id: 'HDMI', value: 'HDMI' },
                    { id: 'TV', value: 'TV' },
                    { id: 'RADIO', value: 'RADIO' },
                    { id: 'Youtube', value: 'Youtube' },
                  ]}
                  onChange={onChange}
                  errorMessage={errors.local_recording_details?.source_folder?.message}
                  disabled={channelDisabled}
                  isRequired
                  $fullWidth
                  onBlur={onBlur}
                />
              )}
            />
          </Fld>
          <Fld>
            <Controller
              control={control}
              name="recording_title_setup.folder_name"
              render={({ field: { value, onBlur, onChange } }) => (
                <Input
                  type="text"
                  label="Folder Name"
                  placeholder="GMA7"
                  size="middle"
                  maxLength={32}
                  spellCheck={false}
                  autoComplete="off"
                  value={value || ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  errorMessage={(errors.recording_title_setup as any)?.folder_name?.message}
                  disabled={channelDisabled}
                  isRequired
                  $fullWidth
                />
              )}
            />
          </Fld>
          <Fld>
            <Controller
              control={control}
              name="recording_title_setup.on_off"
              render={({ field: { value, onBlur, onChange } }) => (
                <Select
                  label="Status"
                  placeHolder="Select Status"
                  size="middle"
                  value={value === 1 || value === 0 ? String(value) : undefined}
                  items={[
                    { id: '1', value: 'Active' },
                    { id: '0', value: 'Inactive' },
                  ]}
                  onChange={(selected) => onChange(selected === undefined ? selected : Number(selected))}
                  errorMessage={(errors.recording_title_setup as any)?.on_off?.message}
                  disabled={channelDisabled}
                  $fullWidth
                  onBlur={onBlur}
                />
              )}
            />
          </Fld>
          <Fld>
            <Controller
              control={control}
              name="recording_title_setup.sagap_process"
              render={({ field: { value, onBlur, onChange } }) => (
                <Select
                  label="Is From Sagap Process"
                  placeHolder="Select"
                  size="middle"
                  value={value === true || value === false ? String(value) : undefined}
                  items={[
                    { id: 'true', value: 'Yes' },
                    { id: 'false', value: 'No' },
                  ]}
                  onChange={(selected) => onChange(selected === undefined ? selected : selected === 'true')}
                  errorMessage={(errors.recording_title_setup as any)?.sagap_process?.message}
                  disabled={channelDisabled}
                  $fullWidth
                  onBlur={onBlur}
                />
              )}
            />
          </Fld>
        </Grid3>
        <Note>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-5M12 8v.5" />
          </svg>
          <div>
            These settings tell the system which Sagap machine records this program, where to get the source files, and
            which folder it belongs to.
          </div>
        </Note>
      </CardBody>
    </Card>
  );
};
